import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Popover, Theme, useMediaQuery, useTheme } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import moment, { Moment } from "moment";
import { useAtomicStyles } from "styles";
import {
  GridApi,
  GridCellParams,
  GridColDef,
  GridComparatorFn,
  GridValueFormatterParams,
} from "@material-ui/data-grid";
import {
  AppButton,
  AppLinearProgress,
  ExpandableGridCell,
  EventCard,
  EventsToolbar,
  EventPreview,
} from "components";
import VisibilityIcon from "@material-ui/icons/Visibility";
import EditIcon from "@material-ui/icons/Edit";
import { Event } from "models";
import { useDispatch, useSelector } from "react-redux";
import { actions, selectors } from "redux-store";
import { useCurrentMerchantId, useTypedTranslations } from "hooks";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    columnsContainer: {
      backgroundColor: theme.palette.primary.light,
    },
    inline: {
      display: "inline",
      marginRight: 15,
    },
    previewPopover: {
      pointerEvents: "none",
      maxWidth: 400,
      maxHeight: 300,
    },
  }),
);

const getFormattedDate = (params: GridValueFormatterParams) => {
  return (params.value as moment.Moment).format("LLLL");
};

const renderImageBanner = (params: GridCellParams) => {
  const api: GridApi = params.api;
  const fields = api.getAllColumns().map((c) => c.field);

  // Index 1 is the banner image url
  const bannerImgUrl: string = params.getValue(fields[1]) as string;

  const imgContainerStyles: React.CSSProperties = {
    width: "100%",
    height: "80%",
    margin: "2px 2px",
    borderRadius: 5,
    border: "1px solid black",
    overflow: "hidden",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  };
  const bannerImgStyles: React.CSSProperties = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center",
  };

  return (
    <div style={imgContainerStyles}>
      <img src={bannerImgUrl} alt="banner" style={bannerImgStyles} />
    </div>
  );
};
const renderCellExpand = (params: GridCellParams) => {
  return (
    <ExpandableGridCell
      value={params.value ? params.formattedValue.toString() : ""}
      width={params.colDef.width}
    />
  );
};

const renderProgressionBar = (params: GridCellParams) => {
  const api: GridApi = params.api;
  const fields = api.getAllColumns().map((c) => c.field);

  // Indexes 4 and 5 of fields are the two dates expressed as string
  const validFrom = params.getValue(fields[4]) as Moment;
  const expire = params.getValue(fields[5]) as Moment;

  const percent = Event.getCompletionPercentage(validFrom, expire, moment());

  return <AppLinearProgress percentage={percent} />;
};
const sortProgressionBar: GridComparatorFn = (
  v1,
  v2,
  cellParams1,
  cellParams2,
) => {
  const row1 = cellParams1.row;
  const row2 = cellParams2.row;

  const perc1 = Event.getCompletionPercentage(
    row1.validFrom,
    row1.expire,
    moment(),
  );
  const perc2 = Event.getCompletionPercentage(
    row2.validFrom,
    row2.expire,
    moment(),
  );

  return perc1 - perc2;
};

const renderMobileCard = (params: GridCellParams) => {
  const data = params.row;

  return (
    <EventCard
      event={
        new Event({
          _id: data.id as string,
          title: data.title,
          content: data.content,
          imageUri: data.imageUri,
          validFrom: data.validFrom,
          expire: data.expire,
        })
      }
    />
  );
};

const useEventsList = () => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const classes = useMemo(
    () => ({
      datagrid: {
        root: styles.columnsContainer,
      },
    }),
    [styles],
  );
  const atomicStyles = useAtomicStyles();
  const [t] = useTypedTranslations();
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [openPopoverById, setOpenPopoverById] = useState("");
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const dispatch = useDispatch();

  const merchantId = useCurrentMerchantId();

  const events = useSelector(selectors.getEventsList).map(
    (eventData) => new Event(eventData),
  );

  const activeCount: number = useMemo(() => {
    return events.filter((event) => event.expire.isAfter(moment())).length;
  }, [events]);

  const datagridComponents = useMemo(() => {
    return {
      Toolbar: EventsToolbar,
    };
  }, []);

  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  const handleEditDialogOpen = useCallback(
    (selectedEvent: Event, toEdit: boolean) => {
      setSelectedEvent(selectedEvent);
      dispatch(
        actions.setEditEventDialogState({
          isOpen: true,
          editMode: toEdit,
          selectedEvent: selectedEvent,
        }),
      );
    },
    [setSelectedEvent, dispatch],
  );

  const renderEditButton = useCallback(
    (params: GridCellParams) => {
      const toModifyEvent = events.find((ev) => ev._id === params.id);
      return (
        <AppButton
          startIcon={<EditIcon />}
          onClick={() => handleEditDialogOpen(toModifyEvent, true)}
        >
          Modifica
        </AppButton>
      );
    },
    [events, handleEditDialogOpen],
  );
  const renderPreviewButton = useCallback(
    (params: GridCellParams) => {
      const toShowEvent = events.find((ev) => ev._id === params.id);
      return (
        <>
          <AppButton
            startIcon={<VisibilityIcon />}
            onClick={() => handleEditDialogOpen(toShowEvent, false)}
            onMouseOver={(ev) => {
              setOpenPopoverById(toShowEvent._id);
              setAnchorEl(ev.currentTarget);
            }}
            onMouseOut={() => {
              setOpenPopoverById("");
              setAnchorEl(null);
            }}
          >
            Mostra
          </AppButton>
          <Popover
            open={openPopoverById === params.id}
            anchorEl={anchorEl}
            onClose={() => {
              setOpenPopoverById("");
              setAnchorEl(null);
            }}
            anchorOrigin={{
              vertical: "center",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "center",
              horizontal: "right",
            }}
            className={styles.previewPopover}
          >
            <EventPreview
              event={toShowEvent}
              minWidth={300}
              maxWidth={400}
              minHeight={200}
              maxHeight={300}
            />
          </Popover>
        </>
      );
    },
    [handleEditDialogOpen, events, anchorEl, openPopoverById, styles],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        width: 150,
        type: "string",
        hide: true,
        filterable: false,
      },
      {
        field: "imageUri",
        headerName: "Banner",
        width: 150,
        type: "string",
        filterable: false,
        sortable: false,
        hide: isMobile,
        renderCell: renderImageBanner,
      },
      {
        field: "title",
        headerName: "Titolo",
        width: 150,
        type: "string",
        sortable: false,
        hide: isMobile,
        flex: 1.5,
        renderCell: renderCellExpand,
      },
      {
        field: "content",
        headerName: "Contenuto",
        width: 150,
        type: "string",
        sortable: false,
        hide: true,
        flex: 1,
        renderCell: renderCellExpand,
      },
      {
        field: "validFrom",
        headerName: "Da",
        width: 150,
        type: "string",
        hide: true,
        flex: 1,
        valueFormatter: getFormattedDate,
        renderCell: renderCellExpand,
      },
      {
        field: "expire",
        headerName: "Fino a",
        width: 150,
        type: "string",
        hide: isMobile,
        flex: 1.5,
        valueFormatter: getFormattedDate,
        renderCell: renderCellExpand,
      },
      {
        field: "progress",
        headerName: "Progresso",
        width: 150,
        filterable: false,
        hide: isMobile,
        flex: 0.8,
        renderCell: renderProgressionBar,
        sortComparator: sortProgressionBar,
      },
      {
        field: "previewButton",
        headerName: "Anteprima",
        width: 120,
        filterable: false,
        sortable: false,
        hide: isMobile,
        renderCell: renderPreviewButton,
      },
      {
        field: "editButton",
        headerName: "Modifica",
        width: 120,
        filterable: false,
        sortable: false,
        hide: isMobile,
        renderCell: renderEditButton,
      },
      {
        field: "mobCard",
        headerName: "Vista Mobile",
        filterable: false,
        sortable: false,
        width: isMobile ? undefined : 0,
        flex: 1,
        hide: !isMobile,
        renderCell: renderMobileCard,
      },
    ],
    [isMobile, renderEditButton, renderPreviewButton],
  );

  const gridData = useMemo(() => {
    return {
      cols: columns,
      rows: events.map((event) => {
        const { _id, ...eventData } = event;

        return {
          id: _id,
          ...eventData,
        };
      }),
    };
  }, [columns, events]);

  useEffect(() => {
    dispatch(actions.getMerchantsEventsByMerchantId.request({ merchantId }));
  }, [dispatch, merchantId]);

  return {
    styles,
    classes,
    atomicStyles,
    gridData,
    activeCount,
    datagridComponents,
    isMobile,
    selectedEvent,
    t,
  };
};

export { useEventsList as default };
